/**
 * This file has a sister in the marcom project
 * https://github.com/joylifeinc/marcom/blob/master/src/hooks/useGeoLocale.ts
 */

import { TranslationsByNamespace } from '@locales/generated';
import { GetTranslation, getLocalStorage, useTranslation } from '@shared/core';
import { useQueryParamHelper } from '@shared/core/queryString';
import { addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEffect } from 'react';
import { validCountryAlpha2Code } from './useGeoLocale/countries';
import { withWindow } from '@shared/utils/withWindow';

import i18n from '@shared/core/i18n/i18n';
import { languageSupported } from '@shared/core/i18n/utils';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { isCurrencyCodeType } from '@shared/utils/currency/currency.constants';

export const CULTURE_PARAMS = ['culture', 'cul'];
export const CURRENCY_PARAMS = ['currency', 'cur'];
export const LANGUAGE_PARAMS = ['language', 'lang', 'lng', 'l'];

export enum CurrencyCode {
  AUD = 'AUD',
  CAD = 'CAD',
  EUR = 'EUR',
  GBP = 'GBP',
  JPY = 'JPY',
  USD = 'USD',
  NZD = 'NZD',
  SGD = 'SGD',
}

const CURRENCY_CODES = new Set<CurrencyCode>(Object.values(CurrencyCode));

export const isCurrencyCode = (value: string): value is CurrencyCode => {
  return CURRENCY_CODES.has(value as CurrencyCode);
};

export const asCurrencyCode = (value: string | undefined | null): CurrencyCode | undefined => {
  return value && isCurrencyCode(value) ? value : undefined;
};

export enum CultureCode {
  AU = 'AU',
  CA = 'CA',
  NZ = 'NZ',
  DE = 'DE',
  ES = 'ES',
  FR = 'FR',
  IT = 'IT',
  GB = 'GB',
  JP = 'JP', // Japan's ISO 3166-1 alpha-2 country code
  US = 'US',
  SG = 'SG',
}

export enum LanguageCode {
  de = 'de',
  en = 'en',
  es = 'es',
  fr = 'fr',
  it = 'it',
  ja = 'ja',
}

const findMatchingEnumValue = <T>(enumObj: Record<string, T>, key?: string): T | undefined => {
  if (!key) {
    return undefined;
  }

  const actualKey = Object.keys(enumObj).find(val => val?.toLowerCase() === key.toLowerCase());
  return actualKey ? enumObj[actualKey] : undefined;
};

type GeoBasedLocalePayload = Readonly<{
  culture: CultureCode;
  currency: CurrencyCodeType;
  language: LanguageCode;
}>;

type LocaleParamKey = 'culture' | 'currency' | 'language';

const PARAM_ALIASES: Record<LocaleParamKey, readonly string[]> = {
  culture: CULTURE_PARAMS,
  currency: CURRENCY_PARAMS,
  language: LANGUAGE_PARAMS,
};

const DefaultLocalePayload: GeoBasedLocalePayload = {
  currency: CurrencyCode.USD,
  culture: CultureCode.US,
  language: LanguageCode.en,
};

const defaultLocales: ReadonlyArray<GeoBasedLocalePayload> = [
  DefaultLocalePayload,
  {
    culture: CultureCode.AU,
    currency: CurrencyCode.AUD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.CA,
    currency: CurrencyCode.CAD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.CA,
    currency: CurrencyCode.CAD,
    language: LanguageCode.fr,
  },
  {
    culture: CultureCode.DE,
    currency: CurrencyCode.EUR,
    language: LanguageCode.de,
  },
  {
    culture: CultureCode.ES,
    currency: CurrencyCode.EUR,
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.FR,
    currency: CurrencyCode.EUR,
    language: LanguageCode.fr,
  },
  {
    culture: CultureCode.IT,
    currency: CurrencyCode.EUR,
    language: LanguageCode.it,
  },
  {
    culture: CultureCode.US,
    currency: CurrencyCode.USD, // USD is the default currency for US, regardless of the language
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.GB,
    currency: CurrencyCode.GBP,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.JP,
    currency: CurrencyCode.JPY,
    language: LanguageCode.ja,
  },
  {
    culture: CultureCode.NZ,
    currency: CurrencyCode.NZD,
    language: LanguageCode.en,
  },
];

const findClosestMatchingLocale = (
  payload?: Partial<{
    culture: string;
    currency: string;
    language: string;
  }>,
): GeoBasedLocalePayload | undefined => {
  if (!payload) {
    return undefined;
  }

  // handle extended language codes (eg. en-AU, es-US)
  const [languageCode, cultureCode] = payload.language?.split('-') ?? [];

  const culture = findMatchingEnumValue(CultureCode, payload.culture ?? cultureCode);
  const normalizedCurrency = payload.currency?.toUpperCase();
  const currency = isCurrencyCodeType(normalizedCurrency) ? normalizedCurrency : findMatchingEnumValue(CurrencyCode, normalizedCurrency);
  const language = findMatchingEnumValue(LanguageCode, languageCode);

  // Skip processing if no valid parameters found
  if (!culture && !currency && !language) {
    return undefined;
  }

  const countMatchingProperties = (locale: GeoBasedLocalePayload): number => {
    // Only count culture and language for matching, allowing currency to be an override
    return (locale.language === language ? 1 : 0) + (locale.culture === culture ? 1 : 0);
  };

  // Find the locale from defaultLocales that has the most matching culture/language properties
  const bestMatch = defaultLocales.reduce((bestMatch, locale) => {
    const currentScore = countMatchingProperties(locale);
    const bestScore = bestMatch ? countMatchingProperties(bestMatch) : 0;

    return currentScore > bestScore ? locale : bestMatch;
  });

  // If we have a valid currency from the payload, use it as an override; otherwise use the matched locale's currency
  return bestMatch ? { ...bestMatch, currency: currency || bestMatch.currency } : undefined;
};

export const constructLanguageCodeIso15897 = (language: LanguageCode, culture: CultureCode): string => {
  return `${language}-${culture}`;
};

export const updateLocaleLocalStorage = (updatedLocale: Partial<GeoBasedLocalePayload>) => {
  // Use findClosestMatchingLocale to fill in missing fields from partials,
  // then overlay the original values so they're never normalized away
  // (e.g. findClosestMatchingLocale would turn US + fr → US + en).
  const resolved = findClosestMatchingLocale(updatedLocale);
  if (!resolved) {
    return;
  }
  const locale: GeoBasedLocalePayload = {
    culture: updatedLocale.culture ?? resolved.culture,
    currency: updatedLocale.currency ?? resolved.currency,
    language: updatedLocale.language ?? resolved.language,
  };
  getLocalStorage().setItem('joyLocale', JSON.stringify(locale));
};

export const updateLocaleSearchParams = (locale: Partial<Record<LocaleParamKey, string>>, options?: Readonly<{ addParam?: LocaleParamKey }>) => {
  const url = withWindow(global => new URL(global.location.href), undefined);
  if (!url) {
    return;
  }

  const { searchParams } = url;
  let didUpdate = false;

  for (const key of Object.keys(locale) as LocaleParamKey[]) {
    const value = locale[key];
    if (!value) continue;

    const aliases = PARAM_ALIASES[key];
    const paramToSet = aliases.find(a => searchParams.has(a)) ?? (options?.addParam === key ? aliases[0] : undefined);

    if (paramToSet) {
      searchParams.set(paramToSet, value);
      didUpdate = true;
    }
  }

  if (didUpdate) {
    withWindow(global => global.history.replaceState({}, '', url.toString()));
  }
};

const getLocallyStoredGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const localStorage = getLocalStorage();
  const storedLocale = localStorage.getItem('joyLocale');

  if (!storedLocale) {
    return undefined;
  }

  try {
    return findClosestMatchingLocale(JSON.parse(storedLocale));
  } catch (error) {
    addError(error as Error, {});
  }
  return undefined;
};

const useSearchParamsGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const { getValueString } = useQueryParamHelper();

  const culture = getValueString(CULTURE_PARAMS);
  const currency = getValueString(CURRENCY_PARAMS);
  const language = getValueString(LANGUAGE_PARAMS);

  return findClosestMatchingLocale({ culture, currency, language });
};

const useEventLanguageGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const { eventInfo } = useEventInfo();
  const eventLocale = eventInfo?.locale;
  if (!eventLocale) return undefined;
  return findClosestMatchingLocale({ language: eventLocale });
};

const useBrowserLanguageGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const browserLanguage = withWindow(global => global.navigator.language, undefined);

  return findClosestMatchingLocale({ language: browserLanguage });
};

/**
 * Adds languageCodeIso15897, country code (optional), and country name (optional) to the locale
 * @param locale The locale to extend with additional properties.
 * @param countryTranslation A translation accessor that returns localized country names keyed by ISO alpha-2 codes.
 * @returns The extended locale with additional properties.
 */
const extendLocale = (locale: GeoBasedLocalePayload, countryTranslation: GetTranslation<TranslationsByNamespace['countries']>): GeoBasedLocaleExtended => {
  // this variant is for European countries that use EUR as currency but have different cultures, we do not want then to show GB as country code
  const europeScenario = locale.culture === 'GB' && locale.currency === 'EUR';
  const countryCodeAlpha2 = (!europeScenario && validCountryAlpha2Code(locale.culture)) || undefined;
  // europeScenario will have undefined countryCodeAlpha2
  const countryName = countryCodeAlpha2 ? countryTranslation(countryCodeAlpha2)() : undefined;
  return {
    ...locale,
    languageCodeIso15897: constructLanguageCodeIso15897(locale.language, locale.culture),
    countryCodeAlpha2,
    countryName,
  };
};

type GeoBasedLocaleExtended = GeoBasedLocalePayload & {
  languageCodeIso15897: string;
  countryCodeAlpha2?: string;
  countryName?: string;
};

export const useGeoBasedLocale = (defaultValues?: GeoBasedLocalePayload) => {
  const { t } = useTranslation('countries');
  const searchParamLocale = useSearchParamsGeoLocale();
  const eventLocale = useEventLanguageGeoLocale();
  const localStorageLocale = getLocallyStoredGeoLocale();
  const browserLocale = useBrowserLanguageGeoLocale();

  const fallbackLocale = defaultValues ?? DefaultLocalePayload;

  // Language and culture/currency use separate priority chains:
  //
  // Language:        queryParam > eventLocale > localStorage > browser > default
  // Culture/currency: queryParam > localStorage > browser > default
  //
  // Language includes eventLocale so the event owner's configured language is
  // authoritative (and isn't overridden by a browser-locale value that
  // useI18nLocaleSyncer caches to localStorage before eventInfo resolves).
  //
  // Culture/currency excludes eventLocale because currency is geo-based — a USD
  // guest visiting a fr-FR event should still see USD prices, not EUR.
  const geoSourceLocale = searchParamLocale ?? localStorageLocale ?? browserLocale ?? fallbackLocale;
  const geoSourceWithEventLocale = searchParamLocale ?? eventLocale ?? localStorageLocale ?? browserLocale ?? fallbackLocale;

  const locale: GeoBasedLocalePayload = { culture: geoSourceLocale.culture, currency: geoSourceLocale.currency, language: geoSourceWithEventLocale.language };

  return extendLocale(locale, t);
};

const updateLocale = (updatedLocale: GeoBasedLocaleExtended) => {
  updateLocaleLocalStorage(updatedLocale);
  updateLocaleSearchParams({
    culture: updatedLocale.culture,
    currency: updatedLocale.currency,
    language: updatedLocale.languageCodeIso15897,
  });
};

export const isValidCurrencyCode = (value: string): value is CurrencyCode => {
  return Object.values(CurrencyCode).includes(value as CurrencyCode);
};

export const updateLocaleCurrency = (currency: CurrencyCodeType) => {
  const localStorageLocale = getLocallyStoredGeoLocale();
  updateLocaleLocalStorage({ ...localStorageLocale, currency });
  updateLocaleSearchParams({ currency }, { addParam: 'currency' });
};

// sync detected locale to i18n
export const useI18nLocaleSyncer = () => {
  const [translationsEnabled] = useIsFeatureEnabled('joyWebTranslationsEnabled');
  const locale = useGeoBasedLocale();

  const { culture, currency, language } = locale;

  useEffect(() => {
    updateLocale(locale);

    if (translationsEnabled && languageSupported(locale.languageCodeIso15897)) {
      i18n.changeLanguage(locale.languageCodeIso15897);
    }

    // use culture, currency and language in the dependency array to prevent retriggering when the 'locale' object's
    // reference is updated, but the values are the same
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [culture, currency, language, translationsEnabled]);
};
